import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom, timer } from 'rxjs';
import { ChangelogActual, NotaChangelog, VersionApp } from '@app/models/version/version-app.model';

const INTERVALO_POLLING_MS = 5 * 60 * 1000;
const CLAVE_ULTIMA_VERSION_VISTA = 'ultima-version-vista';

@Injectable({
  providedIn: 'root',
})
export class ServicioVersion {
  private _http = inject(HttpClient);

  private _versionInicial: VersionApp | null = null;
  private _changelogActual: ChangelogActual | null = null;

  public hayNuevaVersion = signal(false);
  public hayNovedadesPendientes = signal(false);

  public async inicializar(): Promise<void> {
    this._versionInicial = await this.obtenerVersionRemota();
    this._changelogActual = await this.obtenerChangelogRemoto();

    if (this._versionInicial && this._versionInicial.version !== localStorage.getItem(CLAVE_ULTIMA_VERSION_VISTA)) {
      this.hayNovedadesPendientes.set(true);
    }

    timer(INTERVALO_POLLING_MS, INTERVALO_POLLING_MS).subscribe(() => this.revisarNuevaVersion());
  }

  private async obtenerVersionRemota(): Promise<VersionApp | null> {
    try {
      return await firstValueFrom(this._http.get<VersionApp>(`assets/version.json?t=${Date.now()}`));
    } catch {
      return null;
    }
  }

  private async obtenerChangelogRemoto(): Promise<ChangelogActual | null> {
    try {
      return await firstValueFrom(this._http.get<ChangelogActual>(`assets/changelog-actual.json?t=${Date.now()}`));
    } catch {
      return null;
    }
  }

  private async revisarNuevaVersion(): Promise<void> {
    if (!this._versionInicial) return;

    const versionRemota = await this.obtenerVersionRemota();
    if (versionRemota && versionRemota.commit !== this._versionInicial.commit) {
      this.hayNuevaVersion.set(true);
    }
  }

  public obtenerNotasParaAmbitos(ambitosUsuario: Set<string>): NotaChangelog[] {
    if (!this._changelogActual) return [];

    return this._changelogActual.notas.filter((nota) => {
      if (nota.ambitos.length === 0) return true;
      return nota.ambitos.some((ambito) => ambitosUsuario.has(ambito));
    });
  }

  public obtenerVersionActual(): string | null {
    return this._changelogActual?.version ?? this._versionInicial?.version ?? null;
  }

  public marcarNovedadesComoVistas(): void {
    const version = this.obtenerVersionActual();
    if (version) {
      localStorage.setItem(CLAVE_ULTIMA_VERSION_VISTA, version);
    }
    this.hayNovedadesPendientes.set(false);
  }
}
